/**
 * 엑셀 날짜 셀 → YYYY-MM-DD — 직원 대량 추가 · 대량 수정 업로드가 같이 쓴다.
 *
 * 날짜 셀은 엑셀 안에서 "1900-01-00 부터 센 날 수"(시리얼)로 저장된다. 예전에는 SheetJS 에
 * `cellDates: true` 로 JS Date 를 받아 로컬 시각으로 읽었는데, SheetJS 0.18.5 는 1899년 기준일의
 * 옛 표준시(서울 LMT +8:27:52) 초 단위를 버려서 KST 브라우저에서 2026-09-01 이 2026-08-31 23:59:08 로
 * 들어왔다(하루 앞당겨짐). 그래서 시리얼 숫자를 받아 UTC 산술로만 달력 날짜를 만든다 — 브라우저
 * 시간대와 무관하게 사용자가 입력한 날짜 그대로 나온다.
 */

/** 엑셀 1900 체계의 기준일 — 1900-02-29(존재하지 않는 날, 시리얼 60) 이후 시리얼은 이 날부터 센다 */
const EPOCH_1900_UTC = Date.UTC(1899, 11, 30);
/** 1904 체계(옛 Mac 엑셀) 는 같은 날이 시리얼로 1462 작다 */
const DATE1904_OFFSET = 1462;
const DAY_MS = 86_400_000;
/**
 * 날짜 경계 보정 폭(1분) — SheetJS 로 만든 파일은 위와 같은 이유로 시리얼에 초 단위 오차가 붙는다
 * (46266 대신 46265.9994). 날짜만 받는 칸이라 경계 1분 안쪽이면 가까운 날로 맞춘다.
 */
const SNAP_DAYS = 1 / 1440;

/** 엑셀이 받아 주는 시리얼 상한(9999-12-31) */
const MAX_SERIAL = 2_958_465;

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** 엑셀 날짜 시리얼 → YYYY-MM-DD. 날짜로 볼 수 없는 값이면 빈 문자열. */
export function excelSerialToDateStr(serial: number, opts: { date1904?: boolean } = {}): string {
  if (!Number.isFinite(serial) || serial < 1 || serial > MAX_SERIAL) {
    return "";
  }
  const nearest = Math.round(serial);
  let day = Math.abs(serial - nearest) < SNAP_DAYS ? nearest : Math.floor(serial);
  if (opts.date1904) {
    day += DATE1904_OFFSET;
  } else if (day === 60) {
    // 1900-02-29 — 엑셀(로터스 호환)에만 있는 날. 실제 달력에 없으므로 날짜로 쓰지 않는다.
    return "";
  } else if (day < 60) {
    // 1900-03-01 이전은 가짜 윤일이 없으니 하루 늦은 기준일에서 센다
    day += 1;
  }
  const d = new Date(EPOCH_1900_UTC + day * DAY_MS);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** YYYY-MM-DD · YYYY/MM/DD · YYYY.MM.DD(끝 점 허용), 월 · 일 한 자리 허용 */
const DATE_TEXT = /^(\d{4})[-./](\d{1,2})[-./](\d{1,2})\.?$/;

/**
 * 셀 값 → YYYY-MM-DD (엑셀 시리얼 · 문자열 모두 수용).
 * 문자열이 위 형식이 아니면 점만 대시로 바꿔 그대로 넘긴다 — 형식 오류는 서버 검증이 행 단위로 알려 준다.
 */
export function excelCellToDateStr(v: unknown, opts: { date1904?: boolean } = {}): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "number") {
    return excelSerialToDateStr(v, opts);
  }
  if (v instanceof Date) {
    // 읽기 옵션을 cellDates:false 로 두어 여기로 오지 않는다 — 혹시 들어오면 로컬 달력 날짜로 쓴다
    if (Number.isNaN(v.getTime())) {
      return "";
    }
    return `${v.getFullYear()}-${pad2(v.getMonth() + 1)}-${pad2(v.getDate())}`;
  }
  const text = String(v).trim();
  const m = DATE_TEXT.exec(text);
  if (m) {
    return `${m[1]}-${pad2(Number(m[2]))}-${pad2(Number(m[3]))}`;
  }
  return text.replace(/\./g, "-").replace(/-$/, "");
}
